<template>
  <div class="dashboard-container">
    <Header :title="headerTitle" :user="currentUser" />
  <main class="page-main">
      <div class="tiles-container">
        <div 
          v-for="tile in visibleTiles" 
          :key="tile.name"
          class="tile"
          @click="tile.action"
          :style="{ background: tile.color }"
        >
          <div class="tile-content">
            <div class="tile-icon">{{ tile.icon }}</div>
            <h3 class="tile-title">{{ tile.title }}</h3>
            <p class="tile-description">{{ tile.description }}</p>
          </div>
        </div>
      </div>
    </main>  
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import { ref, onMounted, computed } from 'vue';
import Header from '../components/Header.vue';
import type { User } from '../services/database';

const currentUser = ref<User | null>(null);
const router = useRouter();

const headerTitle = computed(() => {
  return currentUser.value ? `Welcome, ${currentUser.value.name || 'User'}` : 'Dashboard';
});

onMounted(async () => {
  // Load user data from sessionStorage (no session validation needed)
  try {
    const userStr = sessionStorage.getItem('currentUser');
    if (userStr) {
      currentUser.value = JSON.parse(userStr);
      console.log('Dashboard: User loaded from sessionStorage:', currentUser.value);
    } else {
      console.log('Dashboard: No user data in sessionStorage');
    }
  } catch (error) {
    console.error('Dashboard: Error parsing user data from sessionStorage:', error);
  }
});

// Define all available tiles
const allTiles = [
  {
    name: 'family',
    title: 'Family',
    description: 'Manage family members',
    icon: '👨‍👩‍👧‍👦',
    color: '#4CAF50',
    action: () => router.push('/family'),
    requiredRoles: ['admin', 'parent', 'account']
  },
  {
    name: 'earworms',
    title: 'Earworms',
    description: 'Create and manage earworms',
    icon: '🎵',
    color: '#2196F3',
    action: () => router.push('/earworms'),
    requiredRoles: ['admin', 'parent', 'account', 'child']
  },
  {
    name: 'missions',
    title: 'Missions',
    description: 'View and complete missions',
    icon: '🎯',
    color: '#FF9800',
    action: () => router.push('/missions'),
    requiredRoles: ['admin', 'parent', 'account', 'child']
  },
  {
    name: 'users',
    title: 'Users',
    description: 'Manage all users',
    icon: '👥',
    color: '#9C27B0',
    action: () => router.push('/users'),
    requiredRoles: ['admin']
  }
];

// Filter tiles based on user permissions
const visibleTiles = computed(() => {
  if (!currentUser.value) return [];

  return allTiles.filter(tile => {
    // Check if user has required role
    if (currentUser.value?.isAdmin && tile.requiredRoles.includes('admin')) return true;
    if (currentUser.value?.isAccount && tile.requiredRoles.includes('account')) return true;
    if (currentUser.value?.type === 'parent' && tile.requiredRoles.includes('parent')) return true;
    if (currentUser.value?.type === 'child' && tile.requiredRoles.includes('child')) return true;
    
    return false;
  });
});

function navigateToMissions() {
  router.push({ name: 'missions' });
}
function navigateToUsers() {
  router.push({ name: 'users' });
}
function navigateToFamily() {
  router.push({ name: 'family' });
}
function navigateToEarworms() {
  router.push({ name: 'earworms' });
}
</script>

<style scoped>
.logout-btn {
  padding: 0.5rem 1rem;
  background-color: #666;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.logout-btn:hover {
  background-color: #555;
}

.dashboard-main {
  padding: 2rem;
}

</style>
